// @givmo/assistant-panel — ONE persistent thread, and the turn that is running in it.
//
// ══ ONE THREAD, NOT ONE PER VISIT (D-EP-40) ════════════════════════════════════════════════════
//
// The panel adopts the newest conversation this surface created and keeps talking in it: closing the
// panel, changing page, and reloading the app all leave the conversation alone. Only the CHIP and the
// PROMPTS are scoped to a page. There is no new-conversation control anywhere, deliberately — a
// clear-thread button would contradict the ruling the persistence exists to keep.
//
// The conversation is created LAZILY, on the first send rather than on open. Opening the panel to
// look at it is not a conversation, and creating one on open would litter a person's history with
// empty threads and (because creation seeds a message) give every one of them content.
//
// ══ THE CONTEXT IS STAMPED PER SEND, AND THE WHOLE DICT IS THE UNIT ════════════════════════════
//
// Two context dicts, one vocabulary. `entry_context` is CREATION provenance on the conversation;
// `client_context` rides EVERY send and is where the person actually is. The server reads the
// message's dict first and falls back to the conversation's, so one persistent thread can move
// between pages without the pages blurring together.
//
//   · `surface` selects the tool AUDIENCE. It is a SELECTOR among audiences the caller already
//     holds, never a grant — the server also requires its flag pair and the server-injected
//     organization membership. It is stamped on every send: the PAGE's own surface value when the
//     host supplies one (`pageWireSurface` — the server holds a reply from some pages to a
//     different set of output limits), else the thread's `wireSurface`. CREATION always stamps the
//     thread's `wireSurface`, so adoption keeps finding the one thread whichever page it was born on.
//
//   · ⚠ THE STAMP REPLACES, IT DOES NOT MERGE — and that is the one thing this hook must get right.
//     A send carrying `{surface}` alone does NOT keep the previous send's `fundraiser_id`; a send
//     carrying NOTHING falls all the way back to the conversation's creation context. So walking
//     from a fundraiser page to reports must re-stamp `{surface}` WITHOUT the id: omitting the stamp
//     would resurrect the thread's birth context, and carrying the id forward would keep a
//     fundraiser's recipient names exempt from the server's output guard for the life of the thread,
//     long after the page justifying them is gone. Both failures are silent. `entryContextExtra` is
//     therefore read at SEND time, from the host's current page, never captured.
//
//   · Stamping is safe in either merge order: `client_context` already exists on the send body and
//     is persisted-but-unread until the per-message resolution lands, so the panel needs no lockstep.
//
// ══ THE SERVER'S OPENING MESSAGE ═══════════════════════════════════════════════════════════════
//
// Creation seeds a `seq 0` assistant message carrying a signed consumer greeting. Whether it renders
// is the host's call (`renderServerOpeningMessage`) because it is a disclosure decision, not a layout
// one — see `assistant-panel-config.ts`.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AssistantStreamClient, ASSISTANT_EVENTS, type AssistantAnalyticsEmit } from '@givmo/ai';
import {
  decodeConversation,
  decodeConversations,
  decodeSendAccepted,
  findSurfaceThread,
  type AssistantConversationRow,
} from './assistant-decode';
import {
  cancelTurn,
  createConversation,
  fetchConversation,
  fetchConversations,
  isOrgConsentRequired,
  orgConsentFromError,
  refusedSendMessage,
  sendMessage,
} from './assistant-transport';
import { assistantPanelKeys } from './use-assistant-consent';

/**
 * The context dict for a create or a send.
 *
 * `surface` is written LAST deliberately: it is the term the server resolves the tool audience from,
 * and a host extra that happened to be named `surface` would otherwise silently replace it.
 */
function contextDict(
  wireSurface: string,
  extra: Readonly<Record<string, string>> | undefined,
): Record<string, string> {
  return { ...(extra ?? {}), surface: wireSurface };
}

/** A stable identity for a context dict: its entries in key order. */
function contextKey(context: Readonly<Record<string, string>>): string {
  return JSON.stringify(
    Object.keys(context)
      .sort()
      .map((key) => [key, context[key]]),
  );
}

/**
 * The identity of the context a SEND from the current page would stamp — the same dict the send
 * builds (`contextDict`), so it compares exactly with the identity recorded against a reply.
 *
 * ⚠ The server judges a reply by the context its send stamped (its REGISTER, and the fundraiser
 * whose recipients a draft may name), not by the page it is later read on. So a reply may only be
 * handed to the current page's field when it was asked for under this same context — see
 * `AssistantThread.replySendContexts`.
 */
export function sendContextKey(
  surface: string,
  extra: Readonly<Record<string, string>> | undefined,
): string {
  return contextKey(contextDict(surface, extra));
}

/** The message statuses that mean a turn is still running when the thread is first read. */
const IN_FLIGHT_STATUSES = new Set(['pending', 'streaming']);

/** The server-generated opening message's ordinal. */
export const OPENING_MESSAGE_SEQ = 0;

/** One rendered turn-half. */
export interface ThreadMessage {
  /** React key. A server id, or a local id for a message that has not round-tripped. */
  key: string;
  role: 'user' | 'assistant';
  text: string;
  uiBlocks: readonly unknown[];
}

/** What the panel is doing right now. */
export type ThreadStatus = 'idle' | 'sending' | 'streaming' | 'failed';

/** Why a turn failed (`AssistantThread.failure`). */
export interface ThreadFailure {
  retryable: boolean;
  message: string | null;
}

export interface AssistantThreadDeps {
  wireSurface: string;
  /** The surface stamped on each send for the current page; defaults to `wireSurface`. */
  pageWireSurface?: string;
  entryContextExtra?: Readonly<Record<string, string>>;
  apiBaseUrl: string;
  authHeaders: () => Promise<Record<string, string>> | Record<string, string>;
  renderServerOpeningMessage: boolean;
  emit?: AssistantAnalyticsEmit;
  /** Whether to read history at all. False while the panel is closed. */
  active: boolean;
  /**
   * Entry-context values a thread must ALSO carry to be adopted (the email designer's: one thread per
   * email). Absent, the newest thread of the surface is the thread, as everywhere else.
   */
  threadMatch?: Readonly<Record<string, string>>;
}

export interface AssistantThread {
  /** The thread as rendered: server history plus this session's in-progress turn. */
  messages: readonly ThreadMessage[];
  status: ThreadStatus;
  /** The partial reply, while one is arriving. */
  streamingText: string;
  /** History has not settled yet. */
  historyPending: boolean;
  /** The organization refused the turn. Carries the state the refusal itself supplied, or `null`
   *  when it carried none (this environment serves no instrument), and the ask it refused, so a host
   *  can show it and send it again once the organization has accepted. */
  consentRefusal: { state: unknown; text: string } | null;
  /**
   * Why the last turn failed, while `status` is `'failed'`: `retryable` false for a refusal asking
   * again won't cure now (a usage limit, the assistant paused), with the server's own words for it;
   * true for a transient failure (a timeout, the network), where `message` is the turn's own words
   * when it sent any.
   */
  failure: ThreadFailure | null;
  /**
   * For each reply THIS panel asked for since it mounted: its server message id → the
   * `sendContextKey` its send stamped. A reply absent here (read from history, or produced by a
   * turn this panel re-attached to) has no known send context.
   *
   * ⚠ This is what keeps Insert honest about the server's registers: the host withholds the page's
   * field from a reply that was asked for under a DIFFERENT page context (a recap asked on the
   * reports page, then carried onto a fundraiser page), because the server never held that reply to
   * the rules of the page it would be inserted into.
   */
  replySendContexts: ReadonlyMap<string, string>;
  /** Send a new message. Ignored while a turn is running. */
  send: (text: string) => void;
  /** Ask again after a failure — a genuinely new turn, never a replayed one. */
  retry: () => void;
  /** Ask the running turn to stop. */
  stop: () => void;
  /** Clear a consent refusal once the organization has accepted, so the next send is unblocked. */
  clearConsentRefusal: () => void;
}

/** A client-generated idempotency key. `crypto.randomUUID` where available; a shaped fallback where
 *  it is not (an insecure context, an old jsdom), because the key only has to be unique per send. */
function newClientMsgId(): string {
  const cryptoRef = globalThis.crypto;
  if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID();
  const hex = (n: number) =>
    Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}

/** Render-ready rows from a conversation's server history. */
function toThreadMessages(
  conversation: AssistantConversationRow | null,
  renderOpening: boolean,
): ThreadMessage[] {
  if (conversation === null) return [];
  const rows: ThreadMessage[] = [];
  for (const message of conversation.messages) {
    if (!renderOpening && message.seq === OPENING_MESSAGE_SEQ) continue;
    // A message with no text and no blocks is a turn that produced nothing — an in-flight
    // placeholder, or one that ended without content. Rendering an empty bubble would read as a
    // reply that said nothing.
    if (message.text.length === 0 && message.uiBlocks.length === 0) continue;
    rows.push({
      key: message.id,
      role: message.role === 'user' ? 'user' : 'assistant',
      text: message.text,
      uiBlocks: message.uiBlocks,
    });
  }
  return rows;
}

/** Whether the newest assistant message says a turn is still running. */
function hasRunningTurn(conversation: AssistantConversationRow | null): boolean {
  if (conversation === null) return false;
  const last = conversation.messages.at(-1);
  return last !== undefined && last.role !== 'user' && IN_FLIGHT_STATUSES.has(last.status);
}

export function useAssistantThread(deps: AssistantThreadDeps): AssistantThread {
  const {
    wireSurface,
    pageWireSurface,
    entryContextExtra,
    apiBaseUrl,
    authHeaders,
    renderServerOpeningMessage,
    emit,
    active,
    threadMatch,
  } = deps;
  const queryClient = useQueryClient();
  const matchKey = threadMatch === undefined ? undefined : contextKey(threadMatch);
  const threadKey = useMemo(
    () => assistantPanelKeys.thread(wireSurface, matchKey),
    [wireSurface, matchKey],
  );

  // ── The persistent conversation ────────────────────────────────────────────
  // Two reads rather than one: the list finds THE thread for this surface, the detail carries its
  // messages (the list read carries none). Both are skipped while the panel is closed.
  const listRead = useQuery({
    queryKey: [...threadKey, 'list'],
    queryFn: ({ signal }) => fetchConversations({ signal }),
    enabled: active,
    retry: false,
    staleTime: 0,
  });

  const [createdId, setCreatedId] = useState<string | null>(null);
  const discovered = useMemo(
    () =>
      listRead.data === undefined
        ? null
        : findSurfaceThread(decodeConversations(listRead.data), wireSurface, threadMatch),
    // `matchKey` stands for `threadMatch`: a host's fresh object with the same values is the same match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listRead.data, wireSurface, matchKey],
  );
  const conversationId = createdId ?? discovered?.id ?? null;

  const detailRead = useQuery({
    queryKey: [...threadKey, 'detail', conversationId ?? ''],
    queryFn: ({ signal }) => fetchConversation(conversationId as string, { signal }),
    enabled: active && conversationId !== null,
    retry: false,
    staleTime: 0,
  });

  const history = useMemo(
    () => (detailRead.data === undefined ? null : decodeConversation(detailRead.data)),
    [detailRead.data],
  );

  // ── The turn in flight ─────────────────────────────────────────────────────
  const [status, setStatus] = useState<ThreadStatus>('idle');
  const [streamingText, setStreamingText] = useState('');
  const [localTurn, setLocalTurn] = useState<{ text: string } | null>(null);
  const [consentRefusal, setConsentRefusal] = useState<{ state: unknown; text: string } | null>(
    null,
  );
  const [failure, setFailure] = useState<ThreadFailure | null>(null);
  const [replySendContexts, setReplySendContexts] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );
  const streamRef = useRef<AssistantStreamClient | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  conversationIdRef.current = conversationId;

  // One client for the panel's lifetime: it owns the resume cursor, which must survive a reconnect
  // and be reset (not recreated) for a new turn.
  if (streamRef.current === null) {
    streamRef.current = new AssistantStreamClient({ baseUrl: apiBaseUrl, authHeaders });
  }

  const teardown = useCallback(() => {
    streamRef.current?.teardown();
  }, []);

  useEffect(() => teardown, [teardown]);

  /**
   * Tail one turn to its terminal event.
   *
   * Every terminal settles the UI: `message_complete` finalizes, `turn_failed` shows the quiet line,
   * `turn_cancelled` keeps whatever partial arrived (a person who stopped a reply asked for what was
   * written so far, not for it to vanish). `turn_restarted` is supersession — the partial belongs to
   * an attempt the server abandoned, so it is dropped rather than merged into the next one.
   *
   * A thrown error means the client exhausted its no-progress budget; that is the failed state. The
   * generator ends quietly on teardown, which is not a failure and must not be shown as one.
   */
  const tail = useCallback(
    async (streamPath: string, sentFrom: string | null) => {
      const client = streamRef.current;
      if (client === null) return;
      let text = '';
      let settled = false;
      let replyId: string | null = null;
      try {
        for await (const event of client.stream(streamPath)) {
          if (event.type === 'text_delta') {
            text += event.payload.delta;
            setStreamingText(text);
            continue;
          }
          if (event.type === 'turn_restarted') {
            text = '';
            setStreamingText('');
            continue;
          }
          if (event.type === 'message_complete' || event.type === 'turn_cancelled') {
            settled = true;
            replyId =
              typeof event.payload.message_id === 'string' ? event.payload.message_id : null;
            break;
          }
          if (event.type === 'turn_failed') {
            const words = event.payload.user_message;
            setFailure({
              retryable: event.payload.retryable !== false,
              message: typeof words === 'string' && words.trim().length > 0 ? words : null,
            });
            setStatus('failed');
            emit?.({ name: ASSISTANT_EVENTS.errorShown, params: { code: event.payload.code } });
            return;
          }
        }
      } catch {
        setFailure({ retryable: true, message: null });
        setStatus('failed');
        emit?.({ name: ASSISTANT_EVENTS.errorShown, params: { code: 'stream_exhausted' } });
        return;
      }
      if (!settled) {
        // The generator ended without a terminal: a deliberate teardown (the panel closed, or the
        // component unmounted). Leave the state alone — the reply is still generating server-side and
        // history will carry it.
        return;
      }
      // Record which page context this reply was asked under BEFORE history renders it, so it never
      // renders — even for one frame — as a reply of unknown origin that may take the page's field.
      if (sentFrom !== null && replyId !== null) {
        const id = replyId;
        setReplySendContexts((known) => new Map(known).set(id, sentFrom));
      }
      // ⚠ REFETCH FIRST, THEN CLEAR — the order is what the person sees. Clearing on the terminal
      // event drops the streamed reply and the question above it a full round trip before history
      // carries them, so every completed turn blinks out and back at real latency. (Invisible in
      // jsdom and against an instant mock, which is exactly why it survived to review.) A refetch
      // that fails leaves the streamed text on screen rather than blanking the thread.
      try {
        await queryClient.refetchQueries({ queryKey: threadKey });
      } catch {
        // The reply is still on screen from the stream; history reconciles on the next open.
      }
      setStatus('idle');
      setStreamingText('');
      setLocalTurn(null);
    },
    [emit, queryClient, threadKey],
  );

  /**
   * Re-attach to a turn that was already running when the panel opened.
   *
   * `GET …/stream` with no resume cursor answers the conversation's ACTIVE turn, so a person who
   * closed the panel mid-reply and came back sees it continue rather than an empty pause. Runs once
   * per open, guarded on the status being idle so it can never race a send.
   */
  const reattachedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!active || conversationId === null || status !== 'idle') return;
    if (!hasRunningTurn(history)) return;
    if (reattachedRef.current === conversationId) return;
    reattachedRef.current = conversationId;
    setStatus('streaming');
    setStreamingText('');
    streamRef.current?.resetCursor();
    emit?.({ name: ASSISTANT_EVENTS.streamResumed });
    // A turn this panel did not send (another tab, or before a reload) has no known send context.
    void tail(`/assistant/conversations/${encodeURIComponent(conversationId)}/stream`, null);
  }, [active, conversationId, history, status, tail, emit]);

  useEffect(() => {
    if (active) return;
    // ⚠ CLOSING MID-TURN MUST RESET THE LOCAL STATE, or the panel wedges. `teardown()` ends the tail
    // and the generator returns WITHOUT a terminal, so `tail()` deliberately leaves the state alone —
    // which strands `status` on `'streaming'`. The reattach effect below then never fires (it gates
    // on `idle`), so a reopened panel shows a frozen partial reply with a stop control in place of
    // send, and the only live control cancels a real server-side turn the person cannot see.
    // Recovering took a full app reload.
    //
    // Resetting here is what makes the documented promise true: the turn keeps running on the
    // server, the reopened panel reads history, and the reattach effect picks the turn back up
    // through the one path that is actually tested.
    reattachedRef.current = null;
    setStatus('idle');
    setStreamingText('');
    setLocalTurn(null);
    teardown();
  }, [active, teardown]);

  const runTurn = useCallback(
    async (text: string) => {
      setConsentRefusal(null);
      setFailure(null);
      setStatus('sending');
      setStreamingText('');
      setLocalTurn({ text });
      try {
        let id = conversationIdRef.current;
        if (id === null) {
          const created = decodeConversation(
            await createConversation(contextDict(wireSurface, entryContextExtra)),
          );
          if (created === null) throw new Error('conversation create returned no id');
          id = created.id;
          conversationIdRef.current = id;
          setCreatedId(id);
          emit?.({ name: ASSISTANT_EVENTS.conversationStarted });
        }
        // Built HERE rather than reused from creation, so it names the page the person is on right
        // now — including by OMITTING an id they have navigated away from, and by carrying the
        // PAGE's surface value where the host distinguishes one.
        const clientContext = contextDict(pageWireSurface ?? wireSurface, entryContextExtra);
        const accepted = decodeSendAccepted(
          await sendMessage(id, {
            text,
            client_msg_id: newClientMsgId(),
            client_context: clientContext,
          }),
        );
        emit?.({ name: ASSISTANT_EVENTS.messageSent });
        if (accepted === null) {
          // A send that answered no stream path is a turn this client cannot follow. Treat it as a
          // failure rather than waiting on a stream that will never open.
          setFailure({ retryable: true, message: null });
          setStatus('failed');
          return;
        }
        setStatus('streaming');
        streamRef.current?.resetCursor();
        await tail(accepted.streamPath, contextKey(clientContext));
      } catch (error) {
        if (isOrgConsentRequired(error)) {
          // Not a failure — a precondition. The refusal carries the sheet's own state, so the card
          // renders from it with no second read.
          setConsentRefusal({ state: orgConsentFromError(error) ?? null, text });
          setStatus('idle');
          setLocalTurn(null);
          return;
        }
        const refused = refusedSendMessage(error);
        setFailure({ retryable: refused === null, message: refused });
        setStatus('failed');
        emit?.({ name: ASSISTANT_EVENTS.errorShown, params: { code: 'send_failed' } });
      }
    },
    [emit, entryContextExtra, pageWireSurface, tail, wireSurface],
  );

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      if (status === 'sending' || status === 'streaming') return;
      void runTurn(trimmed);
    },
    [runTurn, status],
  );

  const retry = useCallback(() => {
    const text = localTurn?.text;
    if (text === undefined || status === 'sending' || status === 'streaming') return;
    void runTurn(text);
  }, [localTurn, runTurn, status]);

  const stop = useCallback(() => {
    const id = conversationIdRef.current;
    if (id === null) return;
    // The request only ASKS; the terminal `turn_cancelled` on the stream is what settles the UI, so
    // the tail is left running and a refusal (nothing in flight) is a race worth ignoring.
    void cancelTurn(id).catch(() => undefined);
  }, []);

  const clearConsentRefusal = useCallback(() => setConsentRefusal(null), []);

  const messages = useMemo(() => {
    const rows = toThreadMessages(history, renderServerOpeningMessage);
    // The question a person just asked is shown immediately, before the server has echoed it back —
    // and it stays visible through a failure, which is the whole point of the quiet retry line.
    if (localTurn !== null) {
      rows.push({ key: 'local-turn', role: 'user', text: localTurn.text, uiBlocks: [] });
    }
    return rows;
  }, [history, localTurn, renderServerOpeningMessage]);

  return {
    messages,
    status,
    streamingText,
    historyPending:
      active && (listRead.isPending || (conversationId !== null && detailRead.isPending)),
    consentRefusal,
    failure: status === 'failed' ? failure : null,
    replySendContexts,
    send,
    retry,
    stop,
    clearConsentRefusal,
  };
}
